import React, {useMemo, useState} from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    Typography,
    Backdrop,
    CircularProgress
} from '@mui/material';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import useFileDownloader from '../../../../utility/hooks/useFileDownloader';
import {
    downloadStudentsCsvReport,
    downloadStudentsXlsxReport,
    downloadEmployeesCsvReport,
    downloadEmployeesXlsxReport
} from '../../../../api/services/filesService';
import FiltersTab from './FiltersTab';
import FieldsTab from './FieldsTab';
import {STUDENT_FIELDS_MAPPING, EMPLOYEE_FIELDS_MAPPING} from '../const/ReportFieldsMapping';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import PropTypes from 'prop-types';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';

/* eslint-disable react/prop-types */

const SettingsSectionBase = ({header, subHeader, headerIcon, children}) => (
    <Stack direction="column" gap={2}>
        <Box>
            <Stack flexDirection="row" gap={1} alignItems="center">
                {headerIcon}
                <Typography variant="h6" fontWeight="bold">
                    {header}
                </Typography>
            </Stack>
            {subHeader && (
                <Typography variant="subtitle2" color="text.secondary">
                    {subHeader}
                </Typography>
            )}
        </Box>
        {children}
    </Stack>
);

SettingsSectionBase.propTypes = {
    header: PropTypes.node.isRequired,
    headerIcon: PropTypes.node,
    subHeader: PropTypes.node,
    children: PropTypes.node,
};

const DownloadReportPopup = ({open, onClose, isCsv, groupFilters}) => {
    const [loading, setLoading] = useState(false);
    const [memberType, setMemberType] = useState(GROUP_MEMBERS_TYPE.students);
    const [excludedFields, setExcludedFields] = useState(
        STUDENT_FIELDS_MAPPING.filter(e => !e.readonly).map(e => e.value)
    );
    const [activeTab, setActiveTab] = useState(0);
    const [strictRecipientsFiltering, setStrictRecipientsFiltering] = useState(false);
    const [settings, setSettings] = useState({
        groupFilters: [],
        studentFilters: [],
        applicationFilters: [],
        enquiryFilters: [],
        employeeFilters: []
    });

    const {data: schoolInfo} = useGetCurrentSchoolQuery();
    const downloadReport = useFileDownloader();
    const filteredGroupFilterModules = useMemo(() => {
        if (!groupFilters?.length) return [];

        const isEmployee = memberType === GROUP_MEMBERS_TYPE.employee;

        if (isEmployee) {
            return (
                groupFilters
                    ?.filter(
                        group =>
                            group.name === 'EMPLOYEES' ||
                            group.name?.toLowerCase().includes('school group') ||
                            group.name?.toLowerCase().includes('my group')
                    )
                    .map(({filters, ...rest}) => ({
                        filters: filters.filter(
                            filter => filter.membersType === GROUP_MEMBERS_TYPE.employee
                        ),
                        ...rest
                    })) || []
            );
        } else {
            return (
                groupFilters
                    ?.filter(m => m.membersType !== GROUP_MEMBERS_TYPE.employee)
                    .map(({filters, ...rest}) => ({
                        filters: filters.filter(
                            filter => filter.membersType !== GROUP_MEMBERS_TYPE.employee
                        ),
                        ...rest
                    })) || []
            );
        }
    }, [groupFilters, memberType]);

    const handleToggleField = field => {
        setExcludedFields(prev =>
            prev.includes(field)
                ? prev.filter(f => f !== field)
                : [...prev, field]
        );
    };

    const handleGenerate = async () => {
        setLoading(true);
        try {
            const downloadFn =
                memberType === GROUP_MEMBERS_TYPE.employee
                    ? isCsv
                        ? downloadEmployeesCsvReport
                        : downloadEmployeesXlsxReport
                    : isCsv
                        ? downloadStudentsCsvReport
                        : downloadStudentsXlsxReport;

            const basePayload = {
                excludedFields,
                filterIds: settings.groupFilters.filter(e => !e.excluded).map(e => e.id),
                excludedFilterIds: settings.groupFilters.filter(e => e.excluded).map(e => e.id),
                strictRecipientsFiltering
            };

            const payload =
                memberType === GROUP_MEMBERS_TYPE.employee
                    ? {
                        ...basePayload,
                        employeeIds: settings.employeeFilters.map(e => e.id)
                    }
                    : {
                        ...basePayload,
                        studentIds: [
                            ...settings.studentFilters.map(e => e.id),
                            ...settings.applicationFilters.map(e => e.id),
                            ...settings.enquiryFilters.map(e => e.id)
                        ]
                    };

            await downloadReport(downloadFn, payload);

            handleClose();
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setExcludedFields(
            STUDENT_FIELDS_MAPPING.filter(e => !e.readonly).map(e => e.value)
        );
        setSettings({
            groupFilters: [],
            studentFilters: [],
            applicationFilters: [],
            enquiryFilters: [],
            employeeFilters: []
        });
        setMemberType(GROUP_MEMBERS_TYPE.students);
        setActiveTab(0)
        onClose();
    };

    const tabs = [
        {
            label: 'Filters',
            component: (
                <FiltersTab
                    data={filteredGroupFilterModules}
                    settings={settings}
                    strictRecipientsFiltering={strictRecipientsFiltering}
                    setStrictRecipientsFiltering={setStrictRecipientsFiltering}
                    setSettings={setSettings}
                    disableEnquirySelect={
                        schoolInfo == null ? true : schoolInfo.disableEnquiriesSync
                    }
                    memberType={memberType}
                />
            )
        },
        {
            label: 'Fields',
            component: (
                <FieldsTab
                    excludedFields={excludedFields}
                    handleToggleField={handleToggleField}
                    setExcludedFields={setExcludedFields}
                    memberType={memberType}
                />
            )
        }
    ];

    const reportTypeOptions = {
        [GROUP_MEMBERS_TYPE.students]: {
            id: GROUP_MEMBERS_TYPE.students,
            label: 'Students',
            icon: <PeopleOutlineOutlinedIcon fontSize="small"/>
        },
        [GROUP_MEMBERS_TYPE.employee]: {
            id: GROUP_MEMBERS_TYPE.employee,
            label: 'Employees',
            icon: <AssignmentIndOutlinedIcon fontSize="small"/>
        }
    };

    return (
        <>
            <AdmicityDialog
                open={open}
                handleClose={handleClose}
                title="Report Generation"
                maxWidth="md"
                actions={[
                    {label: 'Cancel', onClick: handleClose},
                    {
                        label: 'Download',
                        onClick: handleGenerate,
                        disabled:
                            !settings.applicationFilters.length &&
                            !settings.studentFilters.length &&
                            !settings.enquiryFilters.length &&
                            !settings.employeeFilters.length &&
                            !settings.groupFilters.filter(e => !e.excluded).length
                    }
                ]}
            >
                <Box sx={{minHeight: '555px'}}>
                    <Box sx={{mt: 2, mb: 3}}>
                        <SettingsSectionBase
                            header="Report Type"
                            headerIcon={<Groups2OutlinedIcon fontSize="small"/>}
                            subHeader="Choose whether to generate a report for Students or Employees."
                        >
                            <FormControl sx={{minWidth: 220}}>
                                <InputLabel id="report-type-label">Report Type</InputLabel>
                                <Select
                                    labelId="report-type-label"
                                    value={memberType}
                                    label="Report Type"
                                    onChange={e => {
                                        const newType = e.target.value;
                                        setMemberType(newType);

                                        const allFields =
                                            newType === GROUP_MEMBERS_TYPE.employee
                                                ? EMPLOYEE_FIELDS_MAPPING
                                                : STUDENT_FIELDS_MAPPING;

                                        setExcludedFields(allFields.filter(f => !f.readonly).map(f => f.value));

                                        setSettings({
                                            groupFilters: [],
                                            studentFilters: [],
                                            applicationFilters: [],
                                            enquiryFilters: [],
                                            employeeFilters: []
                                        });
                                    }}
                                    renderValue={selected => {
                                        const type = reportTypeOptions[selected];
                                        return (
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {type.icon}
                                                {type.label}
                                            </Box>
                                        );
                                    }}
                                >
                                    {Object.values(reportTypeOptions).map(({id, label, icon}) => (
                                        <MenuItem key={id} value={id}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                {icon}
                                                {label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </SettingsSectionBase>
                    </Box>

                    <Tabs
                        value={activeTab}
                        onChange={(event, newValue) => setActiveTab(newValue)}
                    >
                        {tabs.map(({label}, index) => (
                            <Tab key={index} label={label}/>
                        ))}
                    </Tabs>

                    <Box marginTop={4}>{tabs[activeTab].component}</Box>
                </Box>
            </AdmicityDialog>

            <Backdrop
                open={loading}
                sx={{
                    zIndex: theme => theme.zIndex.modal + 1,
                    color: '#fff',
                    flexDirection: 'column'
                }}
            >
                <CircularProgress/>
            </Backdrop>
        </>
    );
};

export default DownloadReportPopup;
